<template>
  <div class="restaurant-search-results">
    <!-- Shimmer loading state -->
    <div v-if="isLoading && restaurants.length === 0" class="shimmer-container">
      <div v-for="i in 6" :key="i" class="w-full h-48 mb-4">
        <div class="w-full h-full rounded-3xl shimmer-card relative overflow-hidden">
          <div class="absolute w-full h-full duration-300 shimmer-animation"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!isLoading && restaurants.length === 0" class="empty-state">
      <div class="empty-icon">🏪</div>
      <h3 class="empty-title">Aucun restaurant trouvé</h3>
      <p class="empty-message">
        Essayez d'ajuster votre recherche ou vos filtres pour trouver ce que vous cherchez.
      </p>
    </div>

    <!-- Restaurant results list -->
    <div
      v-else
      class="restaurants-list"
      @scroll="handleScroll"
      ref="scrollContainer"
    >
      <div class="results-header" v-if="restaurants.length > 0">
        <span class="results-count">{{ restaurants.length }} restaurants trouvés</span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
        <RestaurantCard
          v-for="restaurant in restaurants"
          :key="restaurant.id"
          :item="restaurant"
          horizontal
        />
      </div>

      <!-- Loading more indicator -->
      <div v-if="isLoading && restaurants.length > 0" class="loading-more">
        <div class="spinner"></div>
        <span>Chargement...</span>
      </div>

      <!-- End of results -->
      <div v-if="!hasMore && restaurants.length > 0" class="end-message">
        Fin des résultats
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, getCurrentInstance, watch, ref } from 'vue';
import RestaurantCard from '@features/home/components/RestaurantCard.vue';
import '../controller';

export default {
  name: 'RestaurantSearchResultList',
  
  components: {
    RestaurantCard
  },

  props: {
    searchQuery: {
      type: String,
      default: ''
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    autoSearch: {
      type: Boolean,
      default: true
    }
  },

  setup(props) {
    const { proxy } = getCurrentInstance();
    const { $stores, $bus, $constants } = proxy;

    const currentFilters = ref(null);
    let searchDebounceTimeout = null;
    const DEBOUNCE_DELAY = 300;
    
    const isSearching = ref(false);
    const lastSearchQuery = ref(null);
    const lastSearchFilters = ref(null);

    const restaurants = computed(() => {
      return $stores.restaurantSearch?.state?.restaurants || [];
    });

    const isLoading = computed(() => {
      return $stores.restaurantSearch?.state?.loading || false;
    });

    const hasMore = computed(() => {
      return $stores.restaurantSearch?.state?.hasMore || false;
    });


    const getPriceRangeBounds = (priceRangeId) => {
      const priceRanges = $constants?.PRICE_RANGES || [];
      const range = priceRanges.find((p) => p.id === priceRangeId);
      if (range) return { min: range.min, max: range.max };
      return null;
    };

    const performSearch = () => {
      if (!$bus || !$constants) return;

      const currentQuery = props.searchQuery || '';
      const currentFiltersString = JSON.stringify({
        ...props.filters,
        currentFilters: currentFilters.value
      });

      if (
        isSearching.value &&
        lastSearchQuery.value === currentQuery &&
        lastSearchFilters.value === currentFiltersString
      ) {
        return;
      }

      lastSearchQuery.value = currentQuery;
      lastSearchFilters.value = currentFiltersString;
      isSearching.value = true;

      let searchFilters = { ...props.filters };

      if (currentFilters.value) {
        const { sort, priceRange, categories } = currentFilters.value;

        if (categories && categories.length > 0) {
          searchFilters.categoryId = categories[0];
        }

        if (sort) searchFilters.sort = sort;

        if (priceRange) {
          const bounds = getPriceRangeBounds(priceRange);
          if (bounds) {
            searchFilters.minPrice = bounds.min;
            searchFilters.maxPrice = bounds.max;
          }
        }
      }

      $bus.emit($constants.EVENTS.RESTAURANT_SEARCH.REQUEST, {
        searchQuery: props.searchQuery,
        filters: searchFilters,
        page: 1
      });

      setTimeout(() => {
        isSearching.value = false;
      }, 100);
    };

    const handleScroll = (event) => {
      const container = event.target;
      const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

      if (scrollBottom < 100 && hasMore.value && !isLoading.value) {
        $bus?.emit($constants.EVENTS.RESTAURANT_SEARCH.LOAD_MORE);
      }
    };

    const handleFiltersUpdated = (filters) => {
      currentFilters.value = filters;
      if (props.autoSearch) {
        if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
        searchDebounceTimeout = setTimeout(() => {
          performSearch();
        }, DEBOUNCE_DELAY);
      }
    };

    watch(() => props.searchQuery, () => {
      if (props.autoSearch) {
        if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
        searchDebounceTimeout = setTimeout(() => {
          performSearch();
        }, DEBOUNCE_DELAY);
      }
    });

    watch(() => props.filters, () => {
      if (props.autoSearch) {
        if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
        searchDebounceTimeout = setTimeout(() => {
          performSearch();
        }, DEBOUNCE_DELAY);
      }
    }, { deep: true });

    onMounted(() => {
      const searchFiltersStore = $stores?.searchFilters;
      if (searchFiltersStore?.state?.filters) {
        currentFilters.value = searchFiltersStore.state.filters;
      }

      if ($bus && $constants) {
        $bus.on($constants.EVENTS.SEARCH_FILTERS.FILTERS_UPDATED, handleFiltersUpdated);
      }

      if (props.autoSearch) {
        performSearch();
      }
    });

    onBeforeUnmount(() => {
      if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
      if ($bus && $constants) {
        $bus.off($constants.EVENTS.SEARCH_FILTERS.FILTERS_UPDATED, handleFiltersUpdated);
      }
    });

    return {
      restaurants,
      isLoading,
      hasMore,
      performSearch,
      handleScroll
    };
  }
};
</script>

<style scoped>
.restaurant-search-results {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shimmer-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shimmer-card {
  background: #f5f5f5;
}

.shimmer-animation {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.empty-message {
  font-size: 14px;
  color: #666;
  max-width: 300px;
}

.restaurants-list {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

.results-header {
  padding: 8px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.results-count {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: #666;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.end-message {
  text-align: center;
  padding: 24px;
  color: #999;
  font-size: 14px;
}
</style>
